import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { useRouter } from 'src/routes/hooks';
import { useAuthContext } from 'src/auth/hooks';
import { usePrevious } from './use-previous';

export type HasId = {
  id: number | string;
};
export function deleteById<T extends HasId>(elems: T[], elem: T): T[] {
  elems.splice(
    elems.findIndex((e) => e.id === elem.id),
    1
  );
  return [...elems];
}

export function replaceById<T extends HasId>(elems: T[], elem: T): T[] {
  const replacement = [...elems];
  let found = false;
  for (let i = 0; i < replacement.length; i += 1) {
    if (replacement[i].id === elem.id) {
      replacement[i] = elem;
      found = true;
      break;
    }
  }
  if (!found) {
    replacement.push(elem);
  }
  return replacement;
}

export type ModelData<T> = {
  loading: boolean;
  data: T | null;
  updateData: <K extends keyof T>(field: K) => (newValue: T[K]) => void;
  setData: Dispatch<SetStateAction<T | null>>;
  error: any;
  loaded: boolean;
  setShouldRefresh: (a: boolean) => void;
};

export default function useModelData<T>(dataLoader: any): ModelData<T> {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<any>();
  const [shouldRefresh, setShouldRefresh] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const prevShouldRefresh = usePrevious(shouldRefresh);
  const router = useRouter();
  const { logout } = useAuthContext();

  const refresh = useCallback(async () => {
    const dataToLoad = dataLoader();

    const keys = Object.keys(dataToLoad);
    const promises: Promise<any>[] = [];
    keys.forEach((key) => {
      const dataOfKey = dataToLoad[key];
      if (dataOfKey instanceof Promise) {
        promises.push(dataOfKey);
      } else {
        promises.push(
          new Promise((resolve) => {
            resolve(dataOfKey);
          })
        );
      }
    }); 

    setLoading(true);
    try {
      const result: any = {};
      const results = await Promise.all(promises);
      for (let i = 0; i < results.length; i += 1) {
        result[keys[i]] = results[i];
      }
      setLoaded(true);
      setLoading(false);
      setData(result);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
        enqueueSnackbar("Your session has expired. Please log in again to continue using the app.", {variant: "error"})
        router.push('/auth/login');
      } else if (err?.response?.status === 404) {
        router.replace('/');
        enqueueSnackbar('Resource that you are trying to access is no longer available', {
          variant: 'warning',
        });
      } else if (err?.response?.status === 403) {
        router.replace('/');
        enqueueSnackbar('You are not allowed to perform that action', { variant: 'warning' });
      } else if (err?.response?.status === 500) {
        // what's crazy is that the home page redirects to a page that throws the exception - causing a forever loop - that sucks - but that's life
        router.replace('/');
        enqueueSnackbar('An unexpected error has occured', { variant: 'error' });
      } else {
        // we probably don't have wifi
        enqueueSnackbar('An unexpected error has occured', { variant: 'error' });
      }
      setLoading(false);
      setError(err);
    }
  }, [dataLoader, router, logout]);

  const [previousLocation, setPreviousLocation] = useState();
  useEffect(() => {
    if (previousLocation !== location) {
      setPreviousLocation(location as any);
      refresh();
    }
  }, [refresh, location, previousLocation, setPreviousLocation]);

  useEffect(() => {
    if (!prevShouldRefresh && shouldRefresh) {
      refresh();
      setShouldRefresh(false);
    }
  }, [refresh, shouldRefresh, prevShouldRefresh]);

  const updateData = useCallback(
    <K extends keyof T>(field: K) =>
      (newValue: T[K]) =>
        setData((d) => ({ ...d, [field]: newValue }) as T),
    [setData]
  );

  return {
    data,
    updateData,
    setData,
    error,
    loaded,
    loading,
    setShouldRefresh,
  };
}
